import React from "react";
import { Link } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { TriageData } from "../TriageWizard";
import { Info, Settings } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface VitalsRiskStepProps {
  data: TriageData;
  updateData: (data: TriageData) => void;
  prefilled?: boolean;
}

const CONDITION_KEYS = [
  { key: 'heartDisease', value: 'heart_disease' },
  { key: 'hypertension', value: 'hypertension' },
  { key: 'diabetes', value: 'diabetes' },
  { key: 'asthma', value: 'asthma' },
  { key: 'copd', value: 'copd' },
  { key: 'chronicKidneyDisease', value: 'chronic_kidney_disease' },
  { key: 'liverDisease', value: 'liver_disease' },
  { key: 'cancer', value: 'cancer' },
  { key: 'immunocompromised', value: 'immunocompromised' },
  { key: 'strokeHistory', value: 'stroke_history' },
  { key: 'seizureDisorder', value: 'seizure_disorder' },
  { key: 'mentalHealthCondition', value: 'mental_health_condition' },
] as const;

const VISIT_KEYS = [
  { key: 'emergency', value: 'emergency' },
  { key: 'urgentCare', value: 'urgent_care' },
  { key: 'primaryCare', value: 'primary_care' },
  { key: 'specialist', value: 'specialist' },
  { key: 'hospitalization', value: 'hospitalization' },
] as const;

const VitalsRiskStep: React.FC<VitalsRiskStepProps> = ({ data, updateData, prefilled }) => {
  const { t } = useLanguage();

  const toggleMedicalHistory = (condition: string) => {
    const medical_history = data.medical_history.includes(condition)
      ? data.medical_history.filter(c => c !== condition)
      : [...data.medical_history, condition];
    updateData({ ...data, medical_history });
  };

  const toggleRecentVisit = (visit: string) => {
    const recent_visits = data.recent_visits.includes(visit)
      ? data.recent_visits.filter(v => v !== visit)
      : [...data.recent_visits, visit];
    updateData({ ...data, recent_visits });
  };

  const updateVitals = (field: string, value: string) => {
    updateData({ ...data, vitals: { ...data.vitals, [field]: value } });
  };

  const handleMedicationsChange = (value: string) => {
    const medications = value.split(',').map(med => med.trim()).filter(med => med.length > 0);
    updateData({ ...data, medications });
  };

  const handleAllergiesChange = (value: string) => {
    const allergies = value.split(',').map(allergy => allergy.trim()).filter(allergy => allergy.length > 0);
    updateData({ ...data, allergies });
  };

  const getConditionLabel = (key: string) => t.vitalsRisk.conditions[key as keyof typeof t.vitalsRisk.conditions] || key;
  const getVisitLabel = (key: string) => t.vitalsRisk.visits[key as keyof typeof t.vitalsRisk.visits] || key;

  return (
    <div className="space-y-6">
      {prefilled && (
        <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg text-sm">
          <Info className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-foreground">{t.vitalsRisk.prefilledNote}</span>
          <Link to="/profile" className="inline-flex items-center gap-1 text-primary hover:underline font-medium ml-auto">
            <Settings className="w-3 h-3" />
            {t.vitalsRisk.updateProfile}
          </Link>
        </div>
      )}

      <div className="space-y-4">
        <Label className="text-base font-medium">{t.vitalsRisk.vitalsTitle}</Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="temperature" className="text-sm">{t.vitalsRisk.temperature}</Label>
            <Input id="temperature" type="number" placeholder="98.6" value={data.vitals.temperature || ""} onChange={(e) => updateVitals("temperature", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="blood_pressure" className="text-sm">{t.vitalsRisk.bloodPressure}</Label>
            <Input id="blood_pressure" placeholder="120/80" value={data.vitals.blood_pressure || ""} onChange={(e) => updateVitals("blood_pressure", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="heart_rate" className="text-sm">{t.vitalsRisk.heartRate}</Label>
            <Input id="heart_rate" type="number" placeholder="70" value={data.vitals.heart_rate || ""} onChange={(e) => updateVitals("heart_rate", e.target.value)} />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <Label className="text-base font-medium">{t.vitalsRisk.medicalHistoryTitle}</Label>
        <div className="grid grid-cols-2 gap-2">
          {CONDITION_KEYS.map(({ key, value }) => (
            <Badge key={value} variant={data.medical_history.includes(value) ? "default" : "outline"} className="cursor-pointer hover:bg-primary/20 transition-colors justify-center p-2 text-center text-sm" onClick={() => toggleMedicalHistory(value)}>
              {getConditionLabel(key)}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="medications" className="text-base font-medium">{t.vitalsRisk.medicationsTitle}</Label>
        <Input id="medications" placeholder={t.vitalsRisk.medicationsPlaceholder} value={data.medications.join(", ")} onChange={(e) => handleMedicationsChange(e.target.value)} />
        <p className="text-xs text-muted-foreground">{t.vitalsRisk.medicationsHint}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="allergies" className="text-base font-medium">{t.vitalsRisk.allergiesTitle}</Label>
        <Input id="allergies" placeholder={t.vitalsRisk.allergiesPlaceholder} value={data.allergies.join(", ")} onChange={(e) => handleAllergiesChange(e.target.value)} />
        <p className="text-xs text-muted-foreground">{t.vitalsRisk.allergiesHint}</p>
      </div>

      <div className="space-y-3">
        <Label className="text-base font-medium">{t.vitalsRisk.recentVisitsTitle}</Label>
        <div className="grid grid-cols-2 gap-2">
          {VISIT_KEYS.map(({ key, value }) => (
            <Badge key={value} variant={data.recent_visits.includes(value) ? "default" : "outline"} className="cursor-pointer hover:bg-primary/20 transition-colors justify-center p-2 text-center text-sm" onClick={() => toggleRecentVisit(value)}>
              {getVisitLabel(key)}
            </Badge>
          ))}
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox id="pregnancy" checked={data.pregnancy || false} onCheckedChange={(checked) => updateData({ ...data, pregnancy: checked as boolean })} />
        <Label htmlFor="pregnancy" className="text-sm font-medium">{t.vitalsRisk.pregnancyLabel}</Label>
      </div>
    </div>
  );
};

export default VitalsRiskStep;
